import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Download } from "lucide-react";

const GRADE_EXCELLENT = 90;
const GRADE_PASSING = 70;
const GRADE_AT_RISK = 50;

function gradeColorClass(grade) {
  if (grade >= GRADE_EXCELLENT) return "text-[var(--gold-bright)]";
  if (grade >= GRADE_PASSING) return "text-[var(--gold)]";
  if (grade >= GRADE_AT_RISK) return "text-[var(--gold-soft)]";
  return "text-red-400";
}

function GradeCell({ entry }) {
  if (!entry) return <td className="px-3 py-2 border-2 border-[var(--gold)]/30 text-center text-xs">—</td>;
  if (!entry.submitted)
    return <td className="px-3 py-2 border-2 border-[var(--gold)]/30 text-center text-xs text-[var(--muted-text)]">missed</td>;
  if (!entry.graded)
    return <td className="px-3 py-2 border-2 border-[var(--gold)]/30 text-center text-xs">pending</td>;
  return <td className={`px-3 py-2 border-2 border-[var(--gold)]/30 text-center text-sm font-bold ${gradeColorClass(entry.grade)}`}>{entry.grade}</td>;
}

export default function GradebookTab({ courseId }) {
  const { user } = useAuth();
  const isInstructor = user?.role === "instructor";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const url = isInstructor ? `/courses/${courseId}/gradebook` : `/courses/${courseId}/my-progress`;
      const res = await api.get(url);
      setData(res.data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [courseId, isInstructor]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="nb-card-static p-5 text-red-400 text-sm">{error}</div>;
  if (!data) return <div className="nb-card-static p-5 text-sm">Loading…</div>;

  const rows = isInstructor ? data.rows : [data.row];

  const downloadCsv = async () => {
    try {
      const res = await api.get(`/courses/${courseId}/gradebook.csv`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gradebook_${courseId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="nb-card-static p-5 overflow-x-auto">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h3 className="font-display text-xl font-bold">{isInstructor ? "Gradebook" : "My progress"}</h3>
        {isInstructor && rows.length > 0 && (
          <button
            data-testid="download-gradebook-csv"
            onClick={downloadCsv}
            className="nb-btn nb-btn-yellow text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Download CSV
          </button>
        )}
      </div>
      {rows.length === 0 && <p className="text-sm text-[var(--muted-text)]">No data yet.</p>}
      {rows.length > 0 && (
        <table data-testid="gradebook-table" className="min-w-full text-sm">
          <thead>
            <tr>
              {isInstructor && <th className="px-3 py-2 text-left border-2 border-[var(--gold)]/30 font-bold text-[var(--gold)]">Student</th>}
              {data.exams.map((e) => (
                <th key={e.id} className="px-3 py-2 border-2 border-[var(--gold)]/30 text-xs font-bold text-[var(--gold)]" title={e.title}>{e.title}</th>
              ))}
              {data.projects.map((p) => (
                <th key={p.id} className="px-3 py-2 border-2 border-[var(--gold)]/30 text-xs font-bold text-[var(--gold)]" title={p.title}>{p.title}</th>
              ))}
              <th className="px-3 py-2 border-2 border-[var(--gold)]/30 text-xs font-bold text-[var(--gold-bright)]">Average</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} data-testid={`gradebook-row-${r.user_id}`}>
                {isInstructor && (
                  <td className="px-3 py-2 border-2 border-[var(--gold)]/30">
                    <p className="font-semibold text-[var(--gold-soft)]">{r.user_name}</p>
                    <p className="text-xs text-[var(--muted-text)]">{r.user_email}</p>
                  </td>
                )}
                {data.exams.map((e) => (
                  <GradeCell key={e.id} entry={r.exams[e.id]} />
                ))}
                {data.projects.map((p) => (
                  <GradeCell key={p.id} entry={r.projects[p.id]} />
                ))}
                <td className="px-3 py-2 border-2 border-[var(--gold)]/30 text-center font-bold">{r.average == null ? "—" : r.average}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
